import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { 
  Shield, 
  FileText, 
  Search,
  ArrowLeft,
  Home,
  User,
  ArrowUpRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDeals({ user, subscription, onLogout }) {
  const { isCollapsed } = useSidebar();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [stageFilter, setStageFilter] = useState("all");

  const stages = [
    { value: "all", label: "All Stages" },
    { value: "new_lead", label: "New Lead" },
    { value: "contacted", label: "Contacted" },
    { value: "analyzing", label: "Analyzing" },
    { value: "offer_sent", label: "Offer Sent" },
    { value: "under_contract", label: "Under Contract" },
    { value: "closed", label: "Closed" },
    { value: "dead", label: "Dead" }
  ];

  useEffect(() => {
    fetchDeals();
  }, [searchTerm, stageFilter]);

  const fetchDeals = async () => {
    try {
      let url = `${BACKEND_URL}/api/admin/deals?`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      if (stageFilter !== "all") url += `stage=${stageFilter}`;
      
      const response = await axios.get(url, { withCredentials: true });
      setDeals(response.data.deals || response.data || []);
    } catch (error) {
      console.error("Failed to fetch deals:", error);
      toast.error("Failed to load deals");
      setDeals([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStageColor = (stage) => {
    const colors = {
      new_lead: "bg-blue-500",
      contacted: "bg-purple-500",
      analyzing: "bg-amber-500",
      offer_sent: "bg-pink-500",
      under_contract: "bg-emerald-500",
      closed: "bg-gold",
      dead: "bg-white/20"
    };
    return colors[stage] || "bg-white/20";
  };

  const getPriorityLabel = (score) => {
    if (score >= 70) return { label: "High", class: "score-high" };
    if (score >= 40) return { label: "Medium", class: "score-medium" };
    return { label: "Low", class: "score-low" };
  };

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold text-white mb-2">Access Denied</h1>
          <p className="text-white/70">You don't have admin privileges.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={`transition-all duration-300 ${isCollapsed ? 'ml-16' : 'ml-64'} p-8`} data-testid="admin-deals">
        {/* Header */}
        <div className="mb-8">
          <Link to="/admin" className="inline-flex items-center gap-2 text-white/70 hover:text-gold mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Admin
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-500/20 border border-purple-500/40 flex items-center justify-center">
              <FileText className="w-6 h-6 text-purple-500" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-white uppercase">Deal Operations</h1>
              <p className="text-white/70">{deals.length} total deals across all users</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/70" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white"
              placeholder="Search by address or owner..."
              data-testid="search-deals"
            />
          </div>
          
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-48 bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08]">
              {stages.map(stage => (
                <SelectItem key={stage.value} value={stage.value} className="text-white hover:bg-white/[0.06]">
                  {stage.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Deals Table */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-4 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.08] overflow-hidden">
            <table className="w-full">
              <thead className="bg-black">
                <tr>
                  <th className="text-left p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Property</th>
                  <th className="text-left p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Owner</th>
                  <th className="text-left p-4 text-xs font-bold text-white/70 uppercase tracking-wider">User</th>
                  <th className="text-left p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Stage</th>
                  <th className="text-left p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Priority</th>
                  <th className="text-left p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Created</th>
                  <th className="text-right p-4 text-xs font-bold text-white/70 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]">
                {deals.map((deal) => {
                  const priority = getPriorityLabel(deal.priority_score);
                  return (
                    <tr key={deal.deal_id} className="hover:bg-white/[0.06]/30">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-white/[0.08] flex items-center justify-center">
                            <Home className="w-5 h-5 text-white/70" />
                          </div>
                          <div>
                            <p className="text-white font-medium truncate max-w-xs">{deal.property_address}</p>
                            <p className="text-sm text-white/70">{deal.property_type}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-white">{deal.owner_name || "—"}</p>
                        <p className="text-sm text-white/70">{deal.owner_phone || "No phone"}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-white text-sm">{deal.user_name}</p>
                        <p className="text-xs text-white/70">{deal.user_email}</p>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${getStageColor(deal.pipeline_stage)}`} />
                          <span className="text-sm text-white/70">
                            {(deal.pipeline_stage || 'new_lead').replace('_', ' ')}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 text-xs font-bold font-mono ${priority.class}`}>
                          {deal.priority_score}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="text-sm text-white/70">{formatDate(deal.created_at)}</span>
                      </td>
                      <td className="p-4 text-right">
                        <Link to={`/admin/deals/${deal.deal_id}`}>
                          <Button variant="ghost" size="sm" className="text-white/70 hover:text-gold">
                            View <ArrowUpRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {deals.length === 0 && (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-white/70 mx-auto mb-3" />
                <p className="text-white/70">No deals found</p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
